import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { useResolvedOwnerId } from '@/hooks/analytics/useResolvedOwnerId';
import { supabase } from '@/integrations/supabase/client';
import { destinationUtmContent } from '@/lib/attribution/target-token';
import { ownerScopedKey } from '@/lib/identity/resolveOwnerId';

/**
 * Everything the post revenue card reads and writes.
 *
 * ## The token problem this hook solves
 *
 * The publisher deliberately never stores the UTM token — it is a pure function
 * of `(post_id, connection_id)`, and a stored copy would be a second source of
 * truth (utm-stamper.ts:41-44). Inverting token → post therefore means
 * RECOMPUTING it, and SQL cannot (FNV-1a over UTF-8 bytes). So this hook reads
 * the post's `social_post_targets` rows, computes one `mora_<token>` per
 * destination client-side, and hands the array to `post_attribution_summary`,
 * which does the join in SQL where the precedence rule lives.
 *
 * ## Identity
 *
 * `social_post_targets` and `post_discount_codes` are account-scoped, so every
 * read resolves the owner first — a shared-account member's raw `user.id`
 * matches nothing. `post_attribution_summary` and `pin_bio_link` additionally
 * resolve the owner SERVER-side via `current_account_owner_id()`, so the client
 * id is a cache key here, never an authorization claim.
 */

const SummarySchema = z.object({
  code_matched_orders: z.number().nullable(),
  code_matched_revenue_cents: z.number().nullable(),
  token_matched_orders: z.number().nullable(),
  token_matched_revenue_cents: z.number().nullable(),
  total_orders: z.number().nullable(),
  total_revenue_cents: z.number().nullable(),
  currency: z.string().nullable(),
  bio_clicks: z.number().nullable(),
  bio_last_token: z.string().nullable(),
  bio_last_destination: z.string().nullable(),
  bio_top_referrer_host: z.string().nullable(),
  last_checked_at: z.string().nullable(),
});

const TargetSchema = z.object({
  connection_id: z.string(),
  platform: z.string(),
});

const CodeSchema = z.object({
  code: z.string(),
  discount_node_gid: z.string().nullable(),
  value_numeric: z.number(),
  ends_at: z.string().nullable(),
  status: z.string(),
  last_error: z.string().nullable(),
  created_at: z.string(),
});

const BioLinkSchema = z.object({
  slug: z.string(),
  destination_post_id: z.string().nullable(),
});

export interface PostAttributionTarget {
  connectionId: string;
  platform: string;
  /** The `mora_<token>` this destination's published links carry. */
  utmContent: string;
}

export interface PostAttribution {
  codeMatched: number;
  codeMatchedRevenueCents: number | null;
  tokenMatched: number;
  tokenMatchedRevenueCents: number | null;
  total: number;
  totalRevenueCents: number | null;
  currency: string | null;
  bioClicks: number;
  bioLastToken: string | null;
  bioLastDestination: string | null;
  bioTopReferrerHost: string | null;
  lastCheckedAt: string | null;
  targets: PostAttributionTarget[];
  discountCode: {
    code: string;
    discountNodeGid: string | null;
    percentage: number;
    endsAt: string | null;
    createdAt: string;
  } | null;
  /** The most recent failed attempt, when there is no active code. Rendered
   *  verbatim so a Shopify error is never lost with a dismissed toast. */
  lastError: string | null;
  bioLink: { slug: string; pinnedToThisPost: boolean } | null;
}

const EMPTY: PostAttribution = {
  codeMatched: 0,
  codeMatchedRevenueCents: null,
  tokenMatched: 0,
  tokenMatchedRevenueCents: null,
  total: 0,
  totalRevenueCents: null,
  currency: null,
  bioClicks: 0,
  bioLastToken: null,
  bioLastDestination: null,
  bioTopReferrerHost: null,
  lastCheckedAt: null,
  targets: [],
  discountCode: null,
  lastError: null,
  bioLink: null,
};

export interface PostAttributionPreflight {
  shopDomain: string | null;
  hasWriteDiscounts: boolean;
  installUrl: string | null;
  adminDiscountsUrl: string | null;
  defaultValue: number;
  /** False when the account has no active Shopify store at all. */
  hasShopify: boolean;
}

interface PreflightResponse {
  ok?: boolean;
  reason?: string;
  shopDomain?: string;
  hasWriteDiscounts?: boolean;
  installUrl?: string;
  adminDiscountsUrl?: string;
  defaultValue?: number;
  error?: string;
}

/**
 * Whether this store has granted `write_discounts`, asked LIVE.
 *
 * Deliberately not inferred from a failed write: a card that only discovers the
 * missing grant after the merchant presses a button is a button that silently
 * 403s, which is the exact failure class this whole build exists to remove.
 *
 * 🔴 `retry: false` IS DELIBERATE, AND IT IS WHY CALLERS MUST READ `isError`.
 * With no retry, one 500 or one dropped connection leaves `data` `undefined`
 * forever — which is byte-identical to `hasShopify: false`. A caller that
 * branches only on `data?.hasShopify` therefore renders "this account has no
 * store" to a merchant who has one, silently, with no way back. The retry stays
 * off (a preflight is a cheap, user-triggered question, not a background poll —
 * three silent retries just delay the honest answer), so the error must be
 * rendered instead. See `PostRevenueCard`'s state 0.
 */
export function usePostAttributionPreflight(enabled = true) {
  const { data: ownerId } = useResolvedOwnerId(enabled);

  return useQuery({
    queryKey: ownerScopedKey(['post-attribution-preflight'], ownerId),
    queryFn: async (): Promise<PostAttributionPreflight> => {
      const { data, error } = await supabase.functions.invoke<PreflightResponse>(
        'shopify-discount-code',
        { body: { action: 'preflight' } },
      );
      if (error) throw new Error(error.message);
      const body = data ?? {};
      return {
        shopDomain: body.shopDomain ?? null,
        hasWriteDiscounts: body.hasWriteDiscounts === true,
        installUrl: body.installUrl ?? null,
        adminDiscountsUrl: body.adminDiscountsUrl ?? null,
        defaultValue: typeof body.defaultValue === 'number' ? body.defaultValue : 0.1,
        hasShopify: body.ok === true,
      };
    },
    enabled: enabled && !!ownerId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });
}

export function postAttributionKey(postId: string, ownerId: string | undefined) {
  return ownerScopedKey(['post-attribution', postId], ownerId);
}

export function usePostAttribution(postId: string | undefined, enabled = true) {
  const { data: ownerId, isLoading: ownerIdLoading } = useResolvedOwnerId(enabled && !!postId);

  const query = useQuery({
    queryKey: postAttributionKey(postId ?? '', ownerId),
    queryFn: async (): Promise<PostAttribution> => {
      const id = postId as string;

      const [targetsResult, codesResult, bioResult] = await Promise.all([
        supabase.from('social_post_targets').select('connection_id, platform').eq('post_id', id),
        supabase
          .from('post_discount_codes')
          .select('code, discount_node_gid, value_numeric, ends_at, status, last_error, created_at')
          .eq('post_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('bio_links')
          .select('slug, destination_post_id')
          .eq('owner_id', ownerId as string),
      ]);

      if (targetsResult.error) throw new Error(targetsResult.error.message);

      const targets: PostAttributionTarget[] = TargetSchema.array()
        .parse(targetsResult.data ?? [])
        .map((t) => ({
          connectionId: t.connection_id,
          platform: t.platform,
          utmContent: destinationUtmContent(id, t.connection_id),
        }));

      const codes = CodeSchema.array().parse(codesResult.data ?? []);
      const active = codes.find((c) => c.status === 'active') ?? null;
      const lastFailure = active ? null : (codes.find((c) => c.status === 'failed') ?? null);

      const bioLinks = BioLinkSchema.array().parse(bioResult.data ?? []);
      const pinnedHere = bioLinks.find((b) => b.destination_post_id === id) ?? null;
      const anyLink = pinnedHere ?? bioLinks[0] ?? null;

      const { data: summaryRows, error: summaryError } = await supabase.rpc(
        'post_attribution_summary',
        {
          p_post_id: id,
          p_tokens: targets.map((t) => t.utmContent),
          p_days: 365,
        },
      );
      if (summaryError) throw new Error(summaryError.message);

      const summary = SummarySchema.array().parse(summaryRows ?? [])[0] ?? null;

      return {
        codeMatched: summary?.code_matched_orders ?? 0,
        codeMatchedRevenueCents: summary?.code_matched_revenue_cents ?? null,
        tokenMatched: summary?.token_matched_orders ?? 0,
        tokenMatchedRevenueCents: summary?.token_matched_revenue_cents ?? null,
        total: summary?.total_orders ?? 0,
        totalRevenueCents: summary?.total_revenue_cents ?? null,
        currency: summary?.currency ?? null,
        bioClicks: summary?.bio_clicks ?? 0,
        bioLastToken: summary?.bio_last_token ?? null,
        bioLastDestination: summary?.bio_last_destination ?? null,
        bioTopReferrerHost: summary?.bio_top_referrer_host ?? null,
        lastCheckedAt: summary?.last_checked_at ?? null,
        targets,
        discountCode: active
          ? {
              code: active.code,
              discountNodeGid: active.discount_node_gid,
              percentage: active.value_numeric,
              endsAt: active.ends_at,
              createdAt: active.created_at,
            }
          : null,
        lastError: lastFailure?.last_error ?? null,
        bioLink: anyLink
          ? { slug: anyLink.slug, pinnedToThisPost: anyLink.destination_post_id === id }
          : null,
      };
    },
    enabled: enabled && !!postId && !!ownerId,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return {
    data: query.data ?? EMPTY,
    isLoading: enabled && !!postId && (ownerIdLoading || query.isLoading),
    // `EMPTY` on error is a lie the CALLER must not render: "no discount code
    // yet" and "we could not find out whether there is one" look the same in
    // this shape, and acting on the first would create a duplicate code in the
    // merchant's store. Callers branch on `isError` BEFORE reading `data`.
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

interface EdgeResponse {
  ok?: boolean;
  reason?: string;
  error?: string;
  code?: string;
  matched?: number;
  checkedAt?: string;
  installUrl?: string;
  usedFallback?: boolean;
}

/**
 * Edge functions in this repo answer 200 with `{ ok:false, error }` for expected
 * failures (missing scope, Shopify userError) so the message survives to the UI
 * instead of becoming a generic network error. Both mutations therefore throw on
 * `ok !== true`, which is what puts the real sentence in front of the merchant.
 */
function unwrap(data: EdgeResponse | null, error: { message: string } | null): EdgeResponse {
  if (error) throw new Error(error.message);
  if (!data?.ok) throw new Error(data?.error ?? 'Something went wrong.');
  return data;
}

export function useCreatePostDiscountCode(postId: string | undefined) {
  const queryClient = useQueryClient();
  const { data: ownerId } = useResolvedOwnerId();

  return useMutation({
    mutationFn: async (percentage: number) => {
      const { data, error } = await supabase.functions.invoke<EdgeResponse>(
        'shopify-discount-code',
        { body: { action: 'create', postId, percentage } },
      );
      return unwrap(data ?? null, error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: postAttributionKey(postId ?? '', ownerId) });
      queryClient.invalidateQueries({
        queryKey: ownerScopedKey(['post-attribution-preflight'], ownerId),
      });
    },
  });
}

export function useReconcilePostSales(postId: string | undefined) {
  const queryClient = useQueryClient();
  const { data: ownerId } = useResolvedOwnerId();

  return useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke<EdgeResponse>(
        'shopify-discount-code',
        { body: { action: 'reconcile', postId } },
      );
      return unwrap(data ?? null, error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: postAttributionKey(postId ?? '', ownerId) });
      // The order now carries a deterministic match, so the account-level
      // "Where your orders come from" card is stale too.
      queryClient.invalidateQueries({ queryKey: ['order-journey'] });
    },
  });
}

export function usePinBioLink(postId: string | undefined) {
  const queryClient = useQueryClient();
  const { data: ownerId } = useResolvedOwnerId();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      const { data, error } = await supabase.rpc('pin_bio_link', {
        p_post_id: postId,
        p_connection_id: connectionId,
      });
      if (error) throw new Error(error.message);
      const row = z.array(z.object({ slug: z.string() })).parse(data ?? [])[0];
      if (!row) throw new Error('Mora could not create your bio link.');
      return row;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: postAttributionKey(postId ?? '', ownerId) });
    },
  });
}

/** The URL a merchant pastes into their profile bio. */
export function bioLinkUrl(slug: string): string {
  return `https://app.mora-marketer.com/functions/v1/bio-link/${slug}`;
}
